/**
 * @fileoverview 1–5 "hands" score picker for the student assessment drawers.
 * Module-local copy of the project's score picker pattern (reuses the shared
 * IconHands SVGs) so the look matches the other assessment drawers. Clicking the
 * selected score again clears it (value 0).
 */

import { Box, Group, Tooltip, UnstyledButton } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { IconHands } from 'components/Icons/IconHands'
import { MAX_SCORE, MIN_SCORE } from '../constants'

const SCORES = Array.from({ length: MAX_SCORE - MIN_SCORE + 1 }, (_, i) => MIN_SCORE + i)

const COLOR_MAP = {
  green: 'var(--mantine-color-green-6)',
  blue: 'var(--mantine-color-blue-6)',
} as const

interface ScorePickerProps {
  value?: number
  onChange: (score: number) => void
  size?: number
  color?: keyof typeof COLOR_MAP
  /** Rubric text per score ("1".."5") shown as a hover tooltip (AR-828). */
  rubrics?: Readonly<Record<string, string>>
}

export function ScorePicker({ value, onChange, size = 56, color = 'green', rubrics }: ScorePickerProps) {
  const { t } = useTranslation()
  const accent = COLOR_MAP[color]
  const greyBorder = 'var(--mantine-color-gray-3)'
  const greyHand = 'var(--mantine-color-gray-7)'

  return (
    <Group spacing="xs" noWrap position="left">
      {SCORES.map(score => {
        const isSelected = value === score
        // Same fallback chain as AssessmentInfo: per-item rubric first, then
        // the generic rubric_1..5 translations.
        const rubric = rubrics?.[String(score)] || t(`rubric_${score}`)
        const button = (
          <UnstyledButton
            key={score}
            onClick={() => onChange(isSelected ? 0 : score)}
            aria-label={`Score ${score}`}
            aria-pressed={isSelected}
            style={{
              position: 'relative',
              width: size,
              height: size,
              borderRadius: 8,
              border: `${isSelected ? 2 : 1}px solid ${isSelected ? accent : greyBorder}`,
              background: 'var(--mantine-color-white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'border-color 120ms ease',
            }}
          >
            <IconHands
              value={score}
              width={Math.round(size * 0.55)}
              height={Math.round(size * 0.55)}
              color={isSelected ? accent : greyHand}
            />
            {isSelected && (
              <Box
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: accent,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconCheck size={10} stroke={3} />
              </Box>
            )}
          </UnstyledButton>
        )

        if (!rubric) return button

        return (
          <Tooltip
            key={score}
            label={rubric}
            withinPortal
            multiline
            width={260}
            position="top"
            events={{ hover: true, focus: true, touch: true }}
          >
            {button}
          </Tooltip>
        )
      })}
    </Group>
  )
}
